import { motion, AnimatePresence } from 'framer-motion'

interface Props {
  open: boolean
  onClose: () => void
}

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)

const stack = ['TypeScript', 'React', 'Node.js', 'n8n', 'Supabase', 'Docker', 'Traefik', 'Claude Code']

export default function Sobre({ open, onClose }: Props) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50,
            }}
          />

          <motion.aside
            key="panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(480px, 100vw)',
              background: 'var(--bg2)',
              borderLeft: '1px solid var(--border)',
              zIndex: 51,
              overflowY: 'auto',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.75rem',
            }}
          >
            <button
              onClick={onClose}
              style={{
                alignSelf: 'flex-end',
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.25rem',
                borderRadius: 4,
              }}
              aria-label="Fechar"
            >
              <IconX />
            </button>

            {/* Avatar + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--bg3)',
                border: '2px solid var(--accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}>
                <img src="/matheus.webp" alt="Matheus Edson" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Matheus Edson</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>Growth Engineer · Uberlândia, BR</div>
              </div>
            </div>

            {/* Bio */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--muted)', fontWeight: 500 }}>
                21 anos. Uberlândia.
              </p>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text)' }}>
                Construí uma <strong>agência de performance</strong> do zero.
                R$40k MRR, R$9M+ em tráfego administrado.
                Full stack de verdade: da campanha no Meta ao servidor que sustenta tudo.
              </p>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text)' }}>
                Dev em uma <strong>empresa de R$350M no setor B2B</strong> durante o dia.
                Infra, segurança e dados.
              </p>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text)' }}>
                Estudo Engenharia da Computação na UFU, 4° período.
                Comecei a trabalhar antes de terminar o curso.
                O mercado ensinou mais rápido que qualquer grade curricular.
              </p>
              <p style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--muted)' }}>
                Publico o processo porque transparência radical constrói mais do que marketing polido.
              </p>
            </div>

            {/* Projects */}
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                Em produção
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { name: 'AIOS', desc: 'Framework de orquestração de agentes IA', href: null },
                  { name: 'Laxtro', desc: 'SaaS de conteúdo: DNA → pauta → roteiro → post', href: 'https://laxtro.com.br/' },
                  { name: 'Agência · stealth', desc: 'Performance GTM, R$40k MRR', href: null },
                ].map(p => (
                  <div
                    key={p.name}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    {p.href ? (
                      <a href={p.href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none' }}>
                        {p.name} ↗
                      </a>
                    ) : (
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.name}</span>
                    )}
                    <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{p.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stack */}
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                Stack
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {stack.map(s => (
                  <span key={s} style={{
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.6rem',
                    borderRadius: 4,
                    background: 'var(--border)',
                    color: 'var(--text)',
                  }}>{s}</span>
                ))}
              </div>
            </div>

            {/* Links */}
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto', flexWrap: 'wrap' }}>
              {[
                { label: 'LinkedIn', href: 'https://www.linkedin.com/in/matheus-edson-linhares-4620b82b1/' },
                { label: 'GitHub', href: 'https://github.com/MatheusEdson' },
                { label: 'Laxtro', href: 'https://laxtro.com.br/' },
              ].map(l => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--accent)',
                    padding: '0.35rem 0.75rem',
                    border: '1px solid rgba(79,126,247,0.4)',
                    borderRadius: 6,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(79,126,247,0.1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
